import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CmpMemberStatusEnum, useGetCmpRejoiningMemberCandidatesQuery, useStartCmpMemberApplicationFromExistingMutation } from "../../../../graphql/types";
import { RoutePaths } from "../../../../enumStrings";
import SearchableSelectField from "../../../common/dashboard/SearchableSelectField";
import { memberRequestApolloContext } from "../memberWorkflowShared";
import { buildStaffErrorFallback, getUserFacingErrorText } from "../../../../utils/userFacingError";

/**
 * IF11 MB9 (CMP Dashboard (21) CD:5905-5914, DCL Rejoining Member): Create
 * Member Step 1 card. Lists Cancelled and Deleted members from the past 365
 * days; picking one prefills the whole form from that member (the same path
 * as Submit New Company Membership Application). The field always shows the
 * placeholder. Shown only where the member status model is on.
 */
const RejoiningMemberCard = ({ chamberId }: { chamberId: string }) => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const { data } = useGetCmpRejoiningMemberCandidatesQuery({
    context: memberRequestApolloContext,
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const [startFromExisting, { loading }] = useStartCmpMemberApplicationFromExistingMutation({ context: memberRequestApolloContext });
  const result = data?.getCmpRejoiningMemberCandidates;
  if (!result?.enabled) return null;

  const pick = async (memberId: string) => {
    const candidate = result.candidates.find((item) => item.memberId === memberId);
    if (!candidate?.ready) return;
    setError("");
    try {
      const response = await startFromExisting({ variables: { input: { chamberId, memberId } } });
      const restart = response.data?.startCmpMemberApplicationFromExisting;
      if (!restart?.member) throw new Error("No prefilled member was returned.");
      navigate(RoutePaths.chamberAdmin_members_create, {
        replace: true,
        state: {
          cmpPrefillMember: restart.member,
          cmpPrefillSourceMemberId: memberId,
          cmpPrefillThirdPartyBillingSnapshot: restart.thirdPartyBillingSnapshot ?? null,
        },
      });
    } catch (caught) {
      setError(getUserFacingErrorText(caught, buildStaffErrorFallback(`load ${candidate.companyName} for rejoining`)));
    }
  };

  return (
    <div className="flex flex-col gap-[12px] rounded-[12px] border border-[#E0E0E0] bg-white px-[28px] py-[26px]">
      <div className="text-[16px] font-semibold text-[#0A0A0A]">Rejoining Member</div>
      <div className="text-[14px] leading-[1.55] text-[#0A0A0A]">
        Search and select a cancelled or deleted member from the past 365 days to rejoin. All data previously stored for this member will be auto-populated. Please review and update any and all required information. New member credentials will automatically be sent to the Primary Voting Member.
      </div>
      <div className="max-w-[520px]">
        <SearchableSelectField
          id="rejoining-member"
          value=""
          onChange={(memberId) => void pick(memberId)}
          disabled={loading}
          loading={loading}
          placeholder="Search Company Name"
          searchPlaceholder="Search Company Name"
          options={result.candidates.map((candidate) => ({
            value: candidate.memberId,
            label: candidate.companyName,
            meta: candidate.memberStatus === CmpMemberStatusEnum.Cancelled ? "Cancelled" : "Deleted",
            disabled: !candidate.ready,
            // Q39: listed but not selectable until the old membership is settled.
            title: candidate.ready ? undefined : candidate.blockedReason || undefined,
          }))}
        />
      </div>
      {error ? <p role="alert" className="text-[13px] text-[#F0202E]">{error}</p> : null}
    </div>
  );
};

export default RejoiningMemberCard;
